/*
  WPC – Collection & search results (sections/wpc-collection-main, sections/wpc-search-main).
  <wpc-collection data-section-id>:
  - filter checkboxes / price / sort update results in place via the Section Rendering API
    (?section_id=...), then swap the [data-wpc-slot] regions and update the URL (back/forward work).
  - active-filter pills, Clear all and pagination links ([data-wpc-ajax]) load the same way.
  - mobile filter drawer (open/close, Esc, backdrop, focus return), group collapse, "Show all".
  Falls back to normal navigation if a fetch fails. Re-init safe (custom element lifecycle).
*/
(() => {
  if (customElements.get('wpc-collection')) return;

  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const PASSTHROUGH = ['_fd', 'pb', 'preview_theme_id'];

  class WpcCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.collapsed = new Set();
      this.expanded = new Set();
      this.abort = null;
      this.priceTimer = null;

      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onMedia = () => DESKTOP.matches && this.closeDrawer(false);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
      this.onPageHide = () => this.closeDrawer(false);
      window.addEventListener('pagehide', this.onPageHide);
      DESKTOP.addEventListener('change', this.onMedia);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('pagehide', this.onPageHide);
      DESKTOP.removeEventListener('change', this.onMedia);
      this.unlockScroll();
    }

    get form() {
      return this.querySelector('[data-wpc-facets-form]');
    }

    /* ---------- params ---------- */
    currentParams() {
      const params = new URLSearchParams();
      const form = this.form;
      if (form) {
        for (const [key, value] of new FormData(form)) {
          if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
        }
        // Drop untouched price bounds so the URL stays clean.
        form.querySelectorAll('[data-wpc-price]').forEach((wrap) => {
          const max = Number(wrap.dataset.max);
          const minInput = wrap.querySelector('[data-wpc-price-input="min"]');
          const maxInput = wrap.querySelector('[data-wpc-price-input="max"]');
          if (minInput && Number(minInput.value) <= 0) params.delete(minInput.name);
          if (maxInput && maxInput.value !== '' && Number(maxInput.value) >= max) params.delete(maxInput.name);
        });
      } else {
        new URLSearchParams(location.search).forEach((v, k) => k !== 'page' && params.append(k, v));
      }
      const sort = this.querySelector('[data-wpc-sort]');
      if (sort && !sort.getAttribute('form')) params.set('sort_by', sort.value);
      params.delete('page');
      return params;
    }

    /* ---------- rendering ---------- */
    async load(url, { push = true, scroll = false } = {}) {
      const target = new URL(url, location.origin);
      // Keep theme-preview params (myshopify.com previews redirect to the primary domain without them).
      const current = new URLSearchParams(location.search);
      PASSTHROUGH.forEach((key) => {
        if (current.has(key) && !target.searchParams.has(key)) target.searchParams.set(key, current.get(key));
      });
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      this.setBusy(true);

      try {
        const res = await fetch(fetchUrl, { signal: this.abort.signal, headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('wpc-collection');
        if (!fresh) throw new Error('Section markup missing');

        const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : '';
        ['facets', 'drawer-foot', 'results'].forEach((name) => {
          const current = this.querySelector(`[data-wpc-slot="${name}"]`);
          const next = fresh.querySelector(`[data-wpc-slot="${name}"]`);
          if (current && next) current.replaceChildren(...next.childNodes);
        });
        this.restoreGroupState();
        this.syncDrawerButton();
        if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });

        if (push) history.pushState({ wpcCollection: this.sectionId }, '', target.pathname + target.search);
        if (scroll) {
          // Horizon 4.3 scrolls .page-wrapper (≥990px) or the document (mobile); scrollIntoView handles both.
          // The offset comes from scroll-margin-top on .wpc-collection__main.
          const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.querySelector('[data-wpc-slot="results"]')?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = target.href;
      } finally {
        this.setBusy(false);
      }
    }

    apply() {
      const qs = this.currentParams().toString();
      this.load(location.pathname + (qs ? `?${qs}` : ''));
    }

    setBusy(busy) {
      const results = this.querySelector('[data-wpc-slot="results"]');
      if (results) results.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.classList.toggle('is-loading', busy);
    }

    /* ---------- events ---------- */
    onChange(event) {
      const el = event.target;
      if (el.matches('[data-wpc-sort]')) return this.apply();
      if (!el.closest('[data-wpc-facets-form]')) return;
      if (el.matches('[data-wpc-price-input]')) {
        this.syncSliderFromInputs(el.closest('[data-wpc-price]'));
        clearTimeout(this.priceTimer);
        return this.apply();
      }
      if (el.matches('[data-wpc-range]')) {
        clearTimeout(this.priceTimer);
        return this.apply();
      }
      if (el.type === 'checkbox') this.apply();
    }

    onInput(event) {
      const el = event.target;
      if (el.matches('[data-wpc-range]')) {
        const wrap = el.closest('[data-wpc-price]');
        const [minR, maxR] = wrap.querySelectorAll('[data-wpc-range]');
        if (Number(minR.value) > Number(maxR.value)) {
          if (el === minR) minR.value = maxR.value;
          else maxR.value = minR.value;
        }
        wrap.querySelector('[data-wpc-price-input="min"]').value = minR.value;
        wrap.querySelector('[data-wpc-price-input="max"]').value = maxR.value;
        this.paintSlider(wrap);
      } else if (el.matches('[data-wpc-price-input]')) {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => {
          this.syncSliderFromInputs(el.closest('[data-wpc-price]'));
          this.apply();
        }, 800);
      }
    }

    onSubmit(event) {
      if (!event.target.matches('[data-wpc-facets-form]')) return;
      event.preventDefault();
      this.apply();
    }

    onClick(event) {
      const link = event.target.closest('a[data-wpc-ajax]');
      if (link && this.contains(link) && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        this.load(link.href, { scroll: link.hasAttribute('data-wpc-scroll') });
        return;
      }
      if (event.target.closest('[data-wpc-drawer-open]')) return this.openDrawer();
      if (event.target.closest('[data-wpc-drawer-close]')) return this.closeDrawer(true);

      const toggle = event.target.closest('[data-wpc-group-toggle]');
      if (toggle) {
        const group = toggle.closest('[data-wpc-group]');
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        this.setGroup(group, open);
        if (open) this.collapsed.delete(group.dataset.wpcGroup);
        else this.collapsed.add(group.dataset.wpcGroup);
        return;
      }

      const more = event.target.closest('[data-wpc-more]');
      if (more) {
        const group = more.closest('[data-wpc-group]');
        const expand = more.getAttribute('aria-expanded') !== 'true';
        this.setMore(group, expand);
        if (expand) this.expanded.add(group.dataset.wpcGroup);
        else this.expanded.delete(group.dataset.wpcGroup);      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-drawer-open')) this.closeDrawer(true);
    }

    onPopState() {
      this.load(location.href, { push: false });
    }

    /* ---------- groups ---------- */
    setGroup(group, open) {
      const toggle = group.querySelector('[data-wpc-group-toggle]');
      toggle.setAttribute('aria-expanded', String(open));
      group.classList.toggle('is-collapsed', !open);
    }

    setMore(group, expand) {
      const more = group.querySelector('[data-wpc-more]');
      const list = group.querySelector('.wpc-facets__list');
      if (!more || !list) return;
      more.setAttribute('aria-expanded', String(expand));
      list.classList.toggle('wpc-facets__list--collapsed', !expand);
      // Label texts come from the markup, so they survive section re-renders.
      const label = more.querySelector('[data-wpc-more-label]');
      if (label) label.textContent = expand ? more.dataset.hideText || 'Show less' : more.dataset.showText || 'Show all';
    }

    /* Re-apply the shopper's open/closed choices after a filter re-render replaced the markup. */
    restoreGroupState() {
      this.querySelectorAll('[data-wpc-group]').forEach((group) => {
        const id = group.dataset.wpcGroup;
        if (this.collapsed.has(id)) this.setGroup(group, false);
        if (this.expanded.has(id)) this.setMore(group, true);
      });
    }

    /* ---------- price slider ---------- */
    syncSliderFromInputs(wrap) {
      if (!wrap) return;
      const max = Number(wrap.dataset.max);
      const [minR, maxR] = wrap.querySelectorAll('[data-wpc-range]');
      const minI = wrap.querySelector('[data-wpc-price-input="min"]');
      const maxI = wrap.querySelector('[data-wpc-price-input="max"]');
      minR.value = minI.value === '' ? 0 : Math.min(Math.max(Number(minI.value), 0), max);
      maxR.value = maxI.value === '' ? max : Math.min(Math.max(Number(maxI.value), 0), max);
      this.paintSlider(wrap);
    }

    paintSlider(wrap) {
      const max = Number(wrap.dataset.max) || 1;
      const [minR, maxR] = wrap.querySelectorAll('[data-wpc-range]');
      const slider = wrap.querySelector('.wpc-facets__slider');
      slider.style.setProperty('--wpc-min', `${(Number(minR.value) / max) * 100}%`);
      slider.style.setProperty('--wpc-max', `${(Number(maxR.value) / max) * 100}%`);
    }

    /* ---------- drawer ---------- */
    openDrawer() {
      const drawer = this.querySelector('[data-wpc-drawer]');
      if (!drawer) return;
      this.lastTrigger = document.activeElement;
      this.classList.add('is-drawer-open');
      this.querySelector('.wpc-collection__backdrop')?.removeAttribute('hidden');
      this.syncDrawerButton();
      this.lockScroll();
      requestAnimationFrame(() => drawer.querySelector('.wpc-collection__drawer-close')?.focus());
    }

    closeDrawer(returnFocus) {
      if (!this.classList.contains('is-drawer-open')) return;
      this.classList.remove('is-drawer-open');
      this.querySelector('.wpc-collection__backdrop')?.setAttribute('hidden', '');
      this.syncDrawerButton();
      this.unlockScroll();
      if (returnFocus) (this.querySelector('[data-wpc-drawer-open]') || this.lastTrigger)?.focus();
    }

    syncDrawerButton() {
      this.querySelector('[data-wpc-drawer-open]')?.setAttribute(
        'aria-expanded',
        String(this.classList.contains('is-drawer-open'))
      );
    }

    /* Shared helper (assets/wpc-scroll-lock.js) when present; class-based fallback otherwise. */
    lockScroll() {
      if (window.wpcScrollLock && typeof window.wpcScrollLock.lock === 'function') {
        window.wpcScrollLock.lock('wpc-filter-drawer');
        this.usedSharedLock = true;
      } else {
        document.documentElement.classList.add('wpc-scroll-lock');
      }
    }

    unlockScroll() {
      if (this.usedSharedLock && window.wpcScrollLock) window.wpcScrollLock.unlock('wpc-filter-drawer');
      this.usedSharedLock = false;
      document.documentElement.classList.remove('wpc-scroll-lock');
    }
  }

  customElements.define('wpc-collection', WpcCollection);
})();
